import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Eye, UserCheck, UserX, Trash2, Building2, Edit, MessageSquare, MapPin, Search, Filter } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import MultiStepPropertyForm from '@/components/property-form/MultiStepPropertyForm';

interface Property {
  id: string;
  title: string;
  type: string;
  monthly_price: number;
  address: string;
  description: string;
  is_active: boolean;
  occupied: boolean;
  views: number;
  created_at: string;
  updated_at: string;
  owner_name?: string;
  owner_email?: string;
  owner_phone?: string;
  rental_requests_count?: number;
  bedrooms?: number;
  bathrooms?: number;
  area?: number;
}

const PropertiesManagement = () => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [filteredProperties, setFilteredProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingProperty, setEditingProperty] = useState<Property | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchProperties = async () => {
    try {
      setLoading(true);
      
      // جلب العقارات مع بيانات المالك وعدد الطلبات
      const { data: propertiesData, error: propertiesError } = await supabase
        .from('properties')
        .select('*')
        .order('created_at', { ascending: false });

      if (propertiesError) throw propertiesError;

      // جلب عدد الطلبات لكل عقار مع بيانات المالك
      const propertiesWithCounts = await Promise.all(
        (propertiesData || []).map(async (property) => {
          // جلب عدد الطلبات
          const { count: requestsCount } = await supabase
            .from('rental_requests')
            .select('*', { count: 'exact', head: true })
            .eq('property_id', property.id);

          // جلب بيانات المالك
          let landlordData = null;
          const { data: landlord } = await supabase
            .from('landlords')
            .select('full_name, email, phone')
            .eq('user_id', property.owner_id)
            .single();
          
          if (landlord) {
            landlordData = landlord;
          }

          return {
            ...property,
            owner_name: landlordData?.full_name || 'غير محدد',
            owner_email: landlordData?.email || '',
            owner_phone: landlordData?.phone || '',
            rental_requests_count: requestsCount || 0
          };
        })
      );

      setProperties(propertiesWithCounts);
      setFilteredProperties(propertiesWithCounts);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ في جلب العقارات",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // تطبيق الفلاتر
  useEffect(() => {
    let filtered = properties;

    // فلتر البحث
    if (searchTerm) {
      filtered = filtered.filter(property => 
        property.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        property.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        property.owner_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // فلتر النوع
    if (filterType !== 'all') {
      filtered = filtered.filter(property => property.type === filterType);
    }

    // فلتر الحالة
    if (filterStatus !== 'all') {
      if (filterStatus === 'active') {
        filtered = filtered.filter(property => property.is_active);
      } else if (filterStatus === 'inactive') {
        filtered = filtered.filter(property => !property.is_active);
      } else if (filterStatus === 'occupied') {
        filtered = filtered.filter(property => property.occupied);
      } else if (filterStatus === 'available') {
        filtered = filtered.filter(property => !property.occupied);
      }
    }

    setFilteredProperties(filtered);
  }, [properties, searchTerm, filterType, filterStatus]);

  const handleTogglePropertyStatus = async (propertyId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('properties')
        .update({ is_active: !currentStatus })
        .eq('id', propertyId);

      if (error) throw error;

      await fetchProperties();
      toast({
        title: "تم بنجاح",
        description: `تم ${!currentStatus ? 'تفعيل' : 'إلغاء تفعيل'} العقار`,
        variant: "default"
      });
    } catch (error) {
      console.error('Error toggling property status:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ في تحديث حالة العقار",
        variant: "destructive"
      });
    }
  };

  const handleDeleteProperty = async (propertyId: string) => {
    if (!confirm('هل أنت متأكد من حذف هذا العقار؟ هذا الإجراء لا يمكن التراجع عنه وسيؤدي إلى حذف جميع البيانات المرتبطة بالعقار.')) {
      return;
    }

    try {
      setLoading(true);
      
      // حذف جميع البيانات المرتبطة بالعقار بالترتيب الصحيح
      
      // 1. حذف تقييمات العقار
      const { error: ratingsError } = await supabase
        .from('property_ratings')
        .delete()
        .eq('property_id', propertyId);

      if (ratingsError) {
        console.error('Error deleting property ratings:', ratingsError);
        throw ratingsError;
      }

      // 2. حذف طلبات الإيجار للعقار
      const { error: requestsError } = await supabase
        .from('rental_requests')
        .delete()
        .eq('property_id', propertyId);

      if (requestsError) {
        console.error('Error deleting rental requests:', requestsError);
        throw requestsError;
      }

      // 3. حذف الإشعارات المرتبطة بالعقار
      const { error: notificationsError } = await supabase
        .from('notifications')
        .delete()
        .eq('property_id', propertyId);

      if (notificationsError) {
        console.error('Error deleting notifications:', notificationsError);
        throw notificationsError;
      }

      // 4. حذف العقارات المحفوظة
      const { error: savedError } = await supabase
        .from('saved_properties')
        .delete()
        .eq('property_id', propertyId);

      if (savedError) {
        console.error('Error deleting saved properties:', savedError);
        throw savedError;
      }

      // 5. حذف صور العقار من قاعدة البيانات والتخزين
      const { data: images } = await supabase
        .from('property_images')
        .select('storage_path')
        .eq('property_id', propertyId);

      if (images && images.length > 0) {
        // حذف الصور من التخزين
        for (const image of images) {
          if (image.storage_path) {
            const { error: storageError } = await supabase.storage
              .from('property-images')
              .remove([image.storage_path]);
            
            if (storageError) {
              console.error('Error deleting image from storage:', storageError);
            }
          }
        }
      }

      // حذف سجلات الصور من قاعدة البيانات
      const { error: imagesError } = await supabase
        .from('property_images')
        .delete()
        .eq('property_id', propertyId);

      if (imagesError) {
        console.error('Error deleting property images:', imagesError);
        throw imagesError;
      }

      // 6. أخيراً، حذف العقار نفسه
      const { error: propertyError } = await supabase
        .from('properties')
        .delete()
        .eq('id', propertyId);

      if (propertyError) {
        console.error('Error deleting property:', propertyError);
        throw propertyError;
      }

      await fetchProperties();
      toast({
        title: "تم بنجاح",
        description: "تم حذف العقار وجميع البيانات المرتبطة به بنجاح",
        variant: "default"
      });
    } catch (error) {
      console.error('Error deleting property:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ في حذف العقار. يرجى المحاولة مرة أخرى.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditProperty = (property: Property) => {
    setEditingProperty(property);
  };

  const handleFormSubmit = () => {
    setEditingProperty(null);
    fetchProperties();
    
    toast({
      title: "تم بنجاح",
      description: "تم تحديث العقار بنجاح",
      variant: "default"
    });
  };

  const handleViewRequests = async (propertyId: string) => {
    // فتح نافذة جديدة لعرض طلبات العقار
    window.open(`/property/${propertyId}`, '_blank');
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  if (loading) {
    return (
      <Card className="shadow-lg">
        <CardContent className="p-6">
          <div className="text-center">جاري تحميل العقارات...</div>
        </CardContent>
      </Card>
    );
  }

  const activeProperties = filteredProperties.filter(p => p.is_active);
  const inactiveProperties = filteredProperties.filter(p => !p.is_active);

  return (
    <div className="space-y-6">
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            إدارة العقارات ({filteredProperties.length})
          </CardTitle>
          <CardDescription>عرض وإدارة جميع العقارات في المنصة</CardDescription>
        </CardHeader>
        <CardContent>
          {/* أدوات البحث والفلترة */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="البحث في العقارات..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger>
                <SelectValue placeholder="نوع العقار" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الأنواع</SelectItem>
                <SelectItem value="شقة">شقة</SelectItem>
                <SelectItem value="فيلا">فيلا</SelectItem>
                <SelectItem value="مكتب">مكتب</SelectItem>
                <SelectItem value="محل">محل</SelectItem>
                <SelectItem value="أخرى">أخرى</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder="حالة العقار" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الحالات</SelectItem>
                <SelectItem value="active">نشط</SelectItem>
                <SelectItem value="inactive">غير نشط</SelectItem>
                <SelectItem value="occupied">مؤجر</SelectItem>
                <SelectItem value="available">متاح</SelectItem>
              </SelectContent>
            </Select>

            <Button 
              variant="outline" 
              onClick={() => {
                setSearchTerm('');
                setFilterType('all');
                setFilterStatus('all');
              }}
            >
              <Filter className="h-4 w-4 mr-2" />
              إعادة تعيين
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* قائمة العقارات */}
      <Tabs defaultValue="active" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="active" className="text-right">
            العقارات النشطة ({activeProperties.length})
          </TabsTrigger>
          <TabsTrigger value="inactive" className="text-right">
            العقارات غير النشطة ({inactiveProperties.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="space-y-4 mt-6">
          {activeProperties.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-gray-600">لا توجد عقارات نشطة</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activeProperties.map((property) => (
                <PropertyCard 
                  key={property.id}
                  property={property}
                  onEdit={handleEditProperty}
                  onDelete={handleDeleteProperty}
                  onToggleStatus={handleTogglePropertyStatus}
                  onViewRequests={handleViewRequests}
                  loading={loading}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="inactive" className="space-y-4 mt-6">
          {inactiveProperties.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-gray-600">لا توجد عقارات غير نشطة</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {inactiveProperties.map((property) => (
                <PropertyCard 
                  key={property.id}
                  property={property}
                  onEdit={handleEditProperty}
                  onDelete={handleDeleteProperty}
                  onToggleStatus={handleTogglePropertyStatus}
                  onViewRequests={handleViewRequests}
                  loading={loading}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* نموذج التعديل المتقدم */}
      {editingProperty && (
        <Dialog open={!!editingProperty} onOpenChange={() => setEditingProperty(null)}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-right">تعديل العقار: {editingProperty.title}</DialogTitle>
            </DialogHeader>
            <MultiStepPropertyForm 
              editingProperty={editingProperty}
              afterSubmit={handleFormSubmit}
              onCancel={() => setEditingProperty(null)}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// مكون بطاقة العقار
const PropertyCard = ({ property, onEdit, onDelete, onToggleStatus, onViewRequests, loading }: any) => {
  const [mainImage, setMainImage] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(true);

  React.useEffect(() => {
    fetchMainImage();
  }, [property.id]);

  const fetchMainImage = async () => {
    try {
      setImageLoading(true);
      console.log('Admin PropertyCard: Fetching image for property:', property.id);

      const { data: images, error } = await supabase
        .from('property_images')
        .select('*')
        .eq('property_id', property.id)
        .order('image_order', { ascending: true });

      if (error) {
        console.error('Admin PropertyCard: Error fetching property images:', error);
        setMainImage(null);
        return;
      }

      console.log('Admin PropertyCard: Found images for property:', property.id, images);

      if (images && images.length > 0) {
        // البحث عن أول صورة لها storage_path صالح
        let mainImageData = images.find(img => img.storage_path && img.storage_path.trim() !== '');
        
        // إذا لم توجد صورة بـ storage_path، ابحث عن الصورة الرئيسية
        if (!mainImageData) {
          mainImageData = images.find(img => img.is_main);
        }
        
        // إذا لم توجد صورة رئيسية، استخدم أول صورة
        if (!mainImageData) {
          mainImageData = images[0];
        }

        let imageUrl: string | null = null;

        // إذا كان هناك storage_path صالح، استخدمه للحصول على الرابط
        if (mainImageData.storage_path && mainImageData.storage_path.trim() !== '') {
          const { data } = supabase.storage
            .from('property-images')
            .getPublicUrl(mainImageData.storage_path);
          
          imageUrl = data.publicUrl;
          console.log('Admin PropertyCard: Using storage URL:', imageUrl);
        } 
        // إذا كان هناك URL مباشر وليس blob، استخدمه
        else if (mainImageData.url && !mainImageData.url.startsWith('blob:')) {
          imageUrl = mainImageData.url;
          console.log('Admin PropertyCard: Using direct URL:', imageUrl);
        }

        setMainImage(imageUrl);
      } else {
        console.log('Admin PropertyCard: No images found for property:', property.id);
        setMainImage(null);
      }
    } catch (error) {
      console.error('Admin PropertyCard: Error in fetchMainImage:', error);
      setMainImage(null);
    } finally {
      setImageLoading(false);
    }
  };

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <div className="relative">
        {/* صورة العقار */}
        <div className="h-48 bg-gray-200 flex items-center justify-center overflow-hidden rounded-t-lg">
          {imageLoading ? (
            <div className="animate-pulse bg-gray-300 w-full h-full flex items-center justify-center">
              <Eye className="h-12 w-12 text-gray-400" />
            </div>
          ) : mainImage ? (
            <img
              src={mainImage}
              alt={property.title}
              className="w-full h-full object-cover"
              onLoad={() => console.log('Admin PropertyCard: Image loaded successfully:', mainImage)}
              onError={(e) => {
                console.error('Admin PropertyCard: Image failed to load:', mainImage);
                setMainImage(null);
              }}
            />
          ) : (
            <div className="text-center text-gray-500">
              <Eye className="h-12 w-12 mx-auto mb-2" />
              <p className="text-sm">صورة العقار</p>
            </div>
          )}
        </div>

        {/* شارات الحالة */}
        <div className="absolute top-2 left-2 flex gap-2">
          <Badge variant={property.is_active ? "default" : "secondary"}>
            {property.is_active ? "نشط" : "غير نشط"}
          </Badge>
          <Badge variant={property.occupied ? "destructive" : "default"} className={property.occupied ? "bg-orange-600" : "bg-green-600"}>
            {property.occupied ? "مؤجر" : "متاح"}
          </Badge>
        </div>
        
        {/* عدد المشاهدات */}
        <div className="absolute top-2 right-2 flex items-center bg-black/50 text-white px-2 py-1 rounded text-sm">
          <Eye className="h-4 w-4 ml-1" />
          {property.views || 0}
        </div>
      </div>

      <CardContent className="p-4">
        <CardTitle className="text-right text-lg mb-2">{property.title}</CardTitle>
        
        <div className="space-y-4">
          {/* معلومات المالك */}
          <div className="bg-blue-50 p-3 rounded-lg">
            <p className="text-sm font-medium text-blue-800">المالك: {property.owner_name}</p>
            <p className="text-xs text-blue-600">{property.owner_email}</p>
            {property.owner_phone && (
              <p className="text-xs text-blue-600">{property.owner_phone}</p>
            )}
          </div>

          <div className="flex items-center text-gray-600 text-right">
            <MapPin className="h-4 w-4 ml-2" />
            <span className="text-sm">{property.address}</span>
          </div>

          <div className="text-right">
            <span className="text-xl font-bold text-green-600">
              {property.monthly_price} ر.ع
            </span>
            <span className="text-gray-500 text-sm mr-1">/ شهرياً</span>
          </div>

          <div className="grid grid-cols-3 gap-2 text-center text-sm">
            <div>
              <p className="text-gray-500">غرف النوم</p>
              <p className="font-semibold">{property.bedrooms}</p>
            </div>
            <div>
              <p className="text-gray-500">الحمامات</p>
              <p className="font-semibold">{property.bathrooms}</p>
            </div>
            <div>
              <p className="text-gray-500">المساحة</p>
              <p className="font-semibold">{property.area} م²</p>
            </div>
          </div>

          {/* إحصائيات */}
          <div className="grid grid-cols-2 gap-2 text-center text-sm bg-gray-50 p-2 rounded">
            <div>
              <p className="text-gray-500">المشاهدات</p>
              <p className="font-semibold text-blue-600">{property.views}</p>
            </div>
            <div>
              <p className="text-gray-500">الطلبات</p>
              <p className="font-semibold text-green-600">{property.rental_requests_count}</p>
            </div>
          </div>

          {/* معلومات إضافية */}
          <div className="text-center text-xs text-gray-500 bg-gray-50 p-2 rounded">
            تاريخ الإضافة: {new Date(property.created_at).toLocaleDateString('en-GB')}
          </div>

          <div className="flex gap-2 justify-end flex-wrap">
            {/* عرض الطلبات */}
            {property.rental_requests_count > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onViewRequests(property.id)}
                disabled={loading}
                className="hover:bg-green-500 hover:text-white"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
            )}

            {/* تعديل */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(property)}
              disabled={loading}
            >
              <Edit className="h-4 w-4" />
            </Button>

            {/* تفعيل/إلغاء تفعيل */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onToggleStatus(property.id, property.is_active)}
              disabled={loading}
              className={property.is_active ? "hover:bg-orange-500 hover:text-white" : "hover:bg-green-500 hover:text-white"}
            >
              {property.is_active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
            </Button>

            {/* حذف */}
            <Button
              variant="destructive"
              size="sm"
              onClick={() => onDelete(property.id)}
              disabled={loading}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PropertiesManagement;